import { Spinner } from "@adafia/ui";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useGetNode } from "@/notes/hooks/useGetNode";
import { useUpdateNote } from "@/notes/hooks/useUpdateNote";
import type { FileNode } from "@/notes/types";
import { Editor } from "./Editor";
import { FolderContentsView } from "./FolderContentsView";

interface NoteEditorProps {
	activeNoteId: string | null;
	onSelect: (node: FileNode | null) => void;
}

export const NoteEditor: React.FC<NoteEditorProps> = React.memo(
	({ activeNoteId, onSelect }) => {
		const { data: activeNote, isLoading: isNoteLoading } =
			useGetNode(activeNoteId);
		const { mutate: updateNote, isPending: isSaving } = useUpdateNote();
		const [showSavingMessage, setShowSavingMessage] = useState(false);
		const [content, setContent] = useState<string>("");
		const [isDirty, setIsDirty] = useState(false);
		const [isAutoSaveEnabled, setIsAutoSaveEnabled] = useState<boolean>(() => {
			const saved = localStorage.getItem("notes-autosave-enabled");
			return saved !== null ? saved === "true" : true;
		});

		// Keep refs to always access the latest values inside the cleanup effect
		const contentRef = useRef(content);
		const activeNoteRef = useRef(activeNote);
		const isDirtyRef = useRef(isDirty);
		const lastNoteId = useRef<string | null>(null);

		useEffect(() => {
			contentRef.current = content;
		}, [content]);

		useEffect(() => {
			activeNoteRef.current = activeNote;
		}, [activeNote]);

		useEffect(() => {
			isDirtyRef.current = isDirty;
		}, [isDirty]);

		// When the note from the server changes, update the local content state
		useEffect(() => {
			if (activeNote) {
				const isDifferentNote = activeNote.id !== lastNoteId.current;
				if (isDifferentNote || !isDirty) {
					setContent(activeNote.content || "");
					setIsDirty(false); // Reset dirty state when a new note is loaded
					lastNoteId.current = activeNote.id;
				}
			}
		}, [activeNote, isDirty]);

		// Auto-save on switch/unmount
		// biome-ignore lint/correctness/useExhaustiveDependencies: We want to run cleanup specifically when activeNoteId changes
		useEffect(() => {
			return () => {
				if (isDirtyRef.current && activeNoteRef.current?.id) {
					const prevNote = activeNoteRef.current;
					updateNote({
						id: prevNote.id,
						content: contentRef.current,
					});
				}
			};
		}, [activeNoteId, updateNote]);

		useEffect(() => {
			let timeoutId: ReturnType<typeof setTimeout> | undefined;
			if (isSaving) {
				setShowSavingMessage(true);
			} else {
				timeoutId = setTimeout(() => {
					setShowSavingMessage(false);
				}, 1500);
			}
			return () => {
				if (timeoutId) clearTimeout(timeoutId);
			};
		}, [isSaving]);

		const handleSave = useCallback(() => {
			if (activeNote?.id && isDirty) {
				const savedContent = content;
				updateNote(
					{
						id: activeNote.id,
						content,
					},
					{
						onSuccess: () => {
							// Only mark clean if content hasn't changed since we initiated the save
							setContent((current) => {
								if (current === savedContent) {
									setIsDirty(false);
								}
								return current;
							});
						},
					},
				);
			}
		}, [activeNote, content, isDirty, updateNote]);

		const togglePublish = () => {
			if (activeNote?.id) {
				const savedContent = content;
				updateNote(
					{
						id: activeNote.id,
						content,
						title: activeNote.title,
						isPublished: !activeNote.isPublished,
					},
					{
						onSuccess: () => {
							setContent((current) => {
								if (current === savedContent) {
									setIsDirty(false);
								}
								return current;
							});
						},
					},
				);
			}
		};

		// Auto-save logic
		useEffect(() => {
			if (isAutoSaveEnabled && isDirty) {
				const timer = setTimeout(() => {
					handleSave();
				}, 2000); // 2 seconds debounce for auto-save
				return () => clearTimeout(timer);
			}
			return undefined;
		}, [isAutoSaveEnabled, isDirty, handleSave]);

		const handleContentChange = (newContent: string) => {
			setIsDirty(true);
			setContent(newContent);
		};

		const toggleAutoSave = () => {
			setIsAutoSaveEnabled((prev) => {
				const next = !prev;
				localStorage.setItem("notes-autosave-enabled", String(next));
				return next;
			});
		};

		if (isNoteLoading) {
			return (
				<div className="no-active-note">
					<Spinner />
					<p>Loading note...</p>
				</div>
			);
		}

		if (!activeNoteId || !activeNote || !activeNote.id) {
			return (
				<div className="no-active-note">
					<p className="no-active-note-title">Select a note to start editing</p>
					<p className="no-active-note-subtitle">
						or create a new one using the plus icon
					</p>
				</div>
			);
		}

		if (activeNote.isFolder) {
			return <FolderContentsView folder={activeNote} onSelect={onSelect} />;
		}

		return (
			<Editor
				key={activeNote.id}
				activeNote={{ ...activeNote, id: activeNote.id, content: content }}
				isSaving={showSavingMessage}
				isDirty={isDirty}
				autoSave={isAutoSaveEnabled}
				onToggleAutoSave={toggleAutoSave}
				onChange={handleContentChange}
				onSave={handleSave}
				onTogglePublish={togglePublish}
			/>
		);
	},
);

NoteEditor.displayName = "NoteEditor";
